
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapPin, Copy, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Location {
  id: string;
  name: string;
  county: string;
  population: number;
  coordinates: string;
  historical_fact: string;
  key_search_terms: string;
}

interface LocationClonerProps {
  blogPost: {
    title: string;
    content: string;
    excerpt: string;
    author: string;
    categories: string[];
    tags: string[];
    seoTitle: string;
    seoDescription: string;
    featuredImage?: string;
  };
  onCloneComplete: () => void;
}

const LocationCloner: React.FC<LocationClonerProps> = ({ blogPost, onCloneComplete }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [isCloning, setIsCloning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('service_locations')
        .select('*')
        .order('name');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load service locations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLocations.length === locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(locations.map(location => location.id));
    }
  };

  const replaceLocationVariables = (content: string, location: Location) => {
    const getLocationClimate = (name: string) => {
      if (name.toLowerCase().includes('florida') || name.toLowerCase().includes('miami')) {
        return 'humid subtropical climate with heavy rainfall and hurricane risks';
      }
      if (name.toLowerCase().includes('texas') || name.toLowerCase().includes('austin')) {
        return 'hot summers and mild winters with occasional severe weather';
      }
      if (name.toLowerCase().includes('california') || name.toLowerCase().includes('san')) {
        return 'Mediterranean climate with dry summers and mild, wet winters';
      }
      return 'varied seasonal weather patterns requiring weather-resistant roofing';
    };

    let replacedText = content;

    const locationMappings = {
      '{locationName}': location.name,
      '{name}': location.name,
      '{locationCounty}': location.county,
      '{county}': location.county,
      '{locationDescription}': location.historical_fact || `the ${location.name} area`,
      '{historical_fact}': location.historical_fact || `This historic community has a rich heritage dating back generations.`,
      '{locationClimate}': getLocationClimate(location.name),
      '{locationPopulation}': location.population?.toString() || 'local community',
      '{population}': location.population?.toString() || 'local community',
      '{locationKeyTerms}': location.key_search_terms || `${location.name} roofing, ${location.county} roofing services`,
      '{key_search_terms}': location.key_search_terms || `${location.name} area`,
      '{coordinates}': location.coordinates,
    };

    Object.entries(locationMappings).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace('{', '\\{').replace('}', '\\}'), 'g');
      replacedText = replacedText.replace(regex, value);
    });

    const businessMappings = {
      '{businessPhone}': '(252) 302-2003',
      '{businessEmail}': 'claims@carolinaTrophyRoofs.com',
      '{businessAddress}': '104 S. Business Ct., Rocky Mount, NC 27804',
      '{businessName}': 'Carolina Trophy Roofs',
      '{businessWebsite}': 'CarolinaTrophyRoofs.com',
      '{locationPhone}': '(252) 371-2266'
    };

    Object.entries(businessMappings).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace('{', '\\{').replace('}', '\\}'), 'g');
      replacedText = replacedText.replace(regex, value);
    });

    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    replacedText = replacedText.replace(/{current_date}/g, currentDate);

    return replacedText;
  };

  const generateOptimizedSlug = (title: string, locationName: string) => {
    // Create SEO-friendly slug: location-service-type
    const cleanTitle = title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    
    const locationSlug = locationName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-');
    
    // Create a unique, SEO-optimized slug
    return `${locationSlug}-${cleanTitle}`;
  };

  const cloneForLocations = async () => {
    if (selectedLocations.length === 0) {
      toast.error('Please select at least one location to clone to');
      return;
    }

    setIsCloning(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const locationId of selectedLocations) {
        const location = locations.find(loc => loc.id === locationId);
        if (!location) continue;

        try {
          const locationContent = replaceLocationVariables(blogPost.content, location);
          const locationTitle = replaceLocationVariables(blogPost.title, location);
          const locationExcerpt = replaceLocationVariables(blogPost.excerpt, location);
          const locationSeoTitle = replaceLocationVariables(blogPost.seoTitle, location);
          const locationSeoDescription = replaceLocationVariables(blogPost.seoDescription, location);

          // Add location-specific tags
          const locationTags = [
            ...blogPost.tags,
            location.name.toLowerCase().replace(/\s+/g, '-'),
            location.county.toLowerCase().replace(/\s+/g, '-'),
            `${location.name.toLowerCase()}-roofing`
          ];

          // Generate optimized slug for better URLs and SEO
          const slug = generateOptimizedSlug(locationTitle, location.name);

          const { error } = await supabase
            .from('blog_posts')
            .insert({
              title: locationTitle,
              content: locationContent,
              excerpt: locationExcerpt,
              slug,
              author: blogPost.author,
              status: 'draft',
              seo_title: locationSeoTitle,
              seo_description: locationSeoDescription,
              categories: blogPost.categories,
              tags: locationTags,
              featured_image: blogPost.featuredImage || null,
            });

          if (error) throw error;
          successCount++;

        } catch (error) {
          console.error(`Error cloning for ${location.name}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully cloned to ${successCount} location${successCount > 1 ? 's' : ''}`);
        onCloneComplete();
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to clone to ${errorCount} location${errorCount > 1 ? 's' : ''}`);
      }

    } catch (error) {
      console.error('Error during cloning process:', error);
      toast.error('Failed to clone posts');
    } finally {
      setIsCloning(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading locations...
        </CardContent>
      </Card>
    );
  }

  const isAllSelected = selectedLocations.length === locations.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Copy className="h-5 w-5 text-blue-500" />
          Clone to Service Locations
        </CardTitle>
        <CardDescription>
          Create location-specific versions with optimized URLs for better performance and SEO
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              Select locations to clone this post to ({selectedLocations.length} selected):
            </Label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              className="text-xs"
            >
              {isAllSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          
          <div className="max-h-64 overflow-y-auto border rounded-md p-3 space-y-2">
            {locations.map((location) => (
              <div key={location.id} className="flex items-center space-x-2">
                <Checkbox
                  id={location.id}
                  checked={selectedLocations.includes(location.id)}
                  onCheckedChange={() => handleLocationToggle(location.id)}
                />
                <Label htmlFor={location.id} className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{location.name}</span>
                    <span className="text-sm text-muted-foreground">({location.county})</span>
                  </div>
                  {location.population && (
                    <div className="text-xs text-muted-foreground ml-6">
                      Population: {location.population.toLocaleString()}
                    </div>
                  )}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <Button 
          onClick={cloneForLocations}
          disabled={selectedLocations.length === 0 || isCloning}
          className="w-full"
        >
          {isCloning ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Cloning to {selectedLocations.length} location{selectedLocations.length > 1 ? 's' : ''}...
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-2" />
              Clone to {selectedLocations.length || 0} Location{selectedLocations.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>

        <div className="text-sm text-muted-foreground p-3 bg-blue-50 rounded-md">
          <p className="font-medium mb-1">🚀 Performance Optimized Cloning:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Creates SEO-friendly URLs (e.g., /blog/raleigh-church-roofing)</li>
            <li>Location-specific content with business details</li>
            <li>Optimized for faster loading and better search rankings</li>
            <li>Individual post URLs reduce database load</li>
            <li>Location-based filtering for better user experience</li>
            <li>All cloned posts start as drafts for review</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default LocationCloner;
